import { Ref, createRef, forwardRef, useCallback, useEffect, useImperativeHandle, useLayoutEffect, useMemo, useRef, useState } from "react";
import { PcSectionSteps, PnlCategoryNames, PnlFsNames, ProductFetchingProgress, TypeOfFinanceOption, financeTabNames, formActionTypes } from "../../../interfaces/interfaces";
import styles from './OtherSections.module.scss';
import MeritoButton from "../../../components/common/MeritoButtons/MeritoButtons";
import { GoPencil as EditIcon } from "react-icons/go";
import { IoMdAdd as AddIcon } from "react-icons/io";
import { useAppDispatch, useAppSelector } from "../../../redux/reduxHooks";
import { VscDebugDisconnect } from "react-icons/vsc";
import { useSearchParams } from "react-router-dom";
import useModal from "../../../components/Hooks/useModal";
import { formatNumberWithComma, getLast12CYMonthsArr } from "../../../components/functions/utilities";
import CategoryForm from "../CategoryForm/CategoryForm";
import { RestS } from "../../../rest/rest-service";
import { setRefreshFinanceStatement } from "../../../redux/financeStatement/financeStatement";
import CardDetails from "./CardDetails";
import { HiArrowNarrowDown as DownArrowIcon } from 'react-icons/hi';
import debounce from 'lodash/debounce'
import TaxInputForm from "./TaxInputForm";
import { AiOutlineStop as StopIcon } from 'react-icons/ai'
import LoanForm from "../LoanForm/LoanForm";
import SecondUserTaxForm from "./SecondUserTaxForm";
import moment from "moment";
import { TbBulb } from "react-icons/tb";


type Props = {
    tabName: PcSectionSteps,
    tabData: any;
    connectionToggle: () => void,
    metricOptions: {
        id: number;
        label: string;
        type: TypeOfFinanceOption,
        value: string;
    }[],
    refreshTabs: () => void;
    financeStatmentOptions: any;
    expiredAccountsConfig: { [x: string]: any };
    productFetchingStatus: { status: ProductFetchingProgress, isProductUpdate: boolean },
    isFirstTimeUser: boolean,
    taxInputRef?: Ref<any>,
    amzAdsData: any[],
    fetchingIpStatus: string[]
}

const manageAccountCategory = ["Ad Spends"];

const subHeadingMapping = {
    "Operating Expenses": "For Ebitda".toUpperCase(),
    "Loan Servicing": "For Net Cash".toUpperCase(),
}


const OtherSections = forwardRef(({ tabName, tabData = null, connectionToggle = () => { }, metricOptions, refreshTabs, financeStatmentOptions, expiredAccountsConfig, productFetchingStatus, isFirstTimeUser, taxInputRef, amzAdsData, fetchingIpStatus }: Props, ref) => {
    const [openForm, setOpenForm] = useState(false);
    const [actionType, setActionType] = useState<formActionTypes>(null);
    const [formCategoryName, setFormCategoryName] = useState(null);
    const [formCategoryId, setFormCategoryId] = useState(null);
    const [formMetricId, setFormMetricId] = useState(null);
    const [financeStatementId, setFinanceStatementId] = useState(null);
    const [openLoanForm, setOpenLoanForm] = useState(false);
    const infoBannerCopy = {
        'Ad Spends': 'Add cost incurred on channels (such as CRED or affiliate channels) if those orders are processed through Shopify.',
        'Sales Channels': 'This is the net sales amount after deducting returns, discounts, cancelled orders and shipping charges collected from the customer.',
        'Platform Commission': 'Add all costs charged by the marketplace, including warehouse and fulfillment costs.'
    }



    // console.log('tabData', tabData)


    const { integrationPartners } = useAppSelector((state) => state.config);

    const { connectionStatus } = useAppSelector((state) => state.connections);


    useEffect(() => {
        // if (update?.updateAvailable) {
        const bodyElement = document.getElementsByTagName("body")[0];
        bodyElement.style.overflowY = "hidden";
        const pageIndexLayout: any = document.getElementById("page_body");
        if (pageIndexLayout) {
            pageIndexLayout.style.overflowY = "hidden";
        }

        return () => {
            const bodyElement = document.getElementsByTagName("body")[0];
            const pageIndexLayout: any = document.getElementById("page_body");
            if (pageIndexLayout) {
                pageIndexLayout.style.overflowY = "scroll";
                pageIndexLayout.style.padding = "1rem 1.5rem 2rem 1.5rem";
            }
        };
        // }
    }, []);



    const closeForm = (fetchTabs = false) => {
        if (formCategoryName == PnlFsNames.loanServicing) {
            setOpenLoanForm(false)
        } else {
            setOpenForm(false);
        }
        setActionType(null);
        setFormMetricId(null);
        setFormCategoryName(null);
        setFinanceStatementId(null);
        if (fetchTabs) {
            refreshTabs();
        }
    }

    const [searchParams, setSearchParams] = useSearchParams();


    const { refreshFinanceStatement } = useAppSelector((state) => state.financeStatement);
    const dispatch = useAppDispatch();

    const sectionRefs = useRef({});
    const cardRefs = useRef({});

    const [showScrollButton, setShowScrollButton] = useState(false);
    const sectionRootContainerRef = useRef(null);

    const [currentVisibleSectionIndex, setCurrentVisibleSectionIndex] = useState(0);

    const [outstandingLoanFsMetricIds, setOutstandingLoanFsMetricIds] = useState(new Set());


    // Determine the currently visible section based on the top 20% criteria
    const updateVisibleSectionIndex = useCallback(() => {
        const container = sectionRootContainerRef.current;
        const visibleThreshold = container.clientHeight * 0.2; // Top 20% height of the container
        let foundSectionIndex = null;

        const sectionKeys = Object.keys(sectionRefs.current);
        if (sectionKeys.includes('taxes')) {
            let index = sectionKeys.indexOf('taxes');

            if (index !== -1) {
                // Remove the element from its current position
                let [element] = sectionKeys.splice(index, 1);

                // Add the element to the start of the array
                sectionKeys.unshift(element);
            }
        }

        sectionKeys.forEach((key, index) => {
            const section = sectionRefs.current[key].current;
            // console.log('section', section, 'container', container);
            if (section && container) {
                const sectionTop = section.offsetTop - container.scrollTop;
                const sectionBottom = sectionTop + section.offsetHeight;
                // console.log('sectionTop', sectionTop, 'sectionBottom', sectionBottom, 'visibleThreshold', visibleThreshold)
                // If any part of the section is within the top 20% of the container
                if (sectionTop < visibleThreshold && sectionBottom > 0) {
                    foundSectionIndex = index;
                }
            }
        });

        // console.log('foundSectionIndex', foundSectionIndex, 'currentVisibleSectionIndex', currentVisibleSectionIndex)

        // Update the state only if a different section is found
        if (foundSectionIndex !== null && foundSectionIndex !== currentVisibleSectionIndex) {
            setCurrentVisibleSectionIndex(foundSectionIndex);
        } else {
            if (foundSectionIndex == null) {
                setCurrentVisibleSectionIndex(0);
            }
        }
    }, [currentVisibleSectionIndex]);

    // Listen to scroll events on the container and update the visible section index
    useEffect(() => {
        const container = sectionRootContainerRef.current;
        container.addEventListener("scroll", updateVisibleSectionIndex, { passive: true });

        // Initial check in case the initial scroll position is not at the top
        updateVisibleSectionIndex();

        return () => {
            container.removeEventListener("scroll", updateVisibleSectionIndex);
        };
    }, [updateVisibleSectionIndex]);

    // Scroll to the next section function, using the currentVisibleSectionIndex
    const scrollToNextSection = useCallback(() => {
        const sectionKeys = Object.keys(sectionRefs.current);
        if (sectionKeys.includes('taxes')) {
            let index = sectionKeys.indexOf('taxes');

            if (index !== -1) {
                // Remove the element from its current position
                let [element] = sectionKeys.splice(index, 1);

                // Add the element to the start of the array
                sectionKeys.unshift(element);
            }
        }
        const nextSectionIndex = currentVisibleSectionIndex + 1; // Calculate the next section index
        const nextSectionKey = sectionKeys[nextSectionIndex]; // Get the key for the next section
        console.log('nextSectionKey', nextSectionKey);
        if (nextSectionKey) {
            const nextSection = sectionRefs.current[nextSectionKey].current;
            setCurrentVisibleSectionIndex(nextSectionIndex);
            nextSection?.scrollIntoView({ behavior: "smooth" });
        }
    }, [currentVisibleSectionIndex]);



    useImperativeHandle(ref, () => ({
        scrollToTax: () => {
            // console.log('sectionRefs', sectionRefs)
            scrollToElement(sectionRefs.current['taxes']);
        }
    }), [sectionRefs])

    const checkScrollButtonVisibility = useCallback(() => {
        const container = sectionRootContainerRef.current;


        const sectionKeys = Object.keys(sectionRefs.current);
        if (sectionKeys.includes('taxes')) {
            let index = sectionKeys.indexOf('taxes');

            if (index !== -1) {
                // Remove the element from its current position
                let [element] = sectionKeys.splice(index, 1);

                // Add the element to the start of the array
                sectionKeys.unshift(element);
            }
        }
        const lastSectionKey = sectionKeys[sectionKeys.length - 1];

        // console.log('lastSectionKey', lastSectionKey);
        const lastSectionRef = sectionRefs.current[lastSectionKey];

        if (container && lastSectionRef && lastSectionRef.current) {
            const { bottom } = lastSectionRef.current.getBoundingClientRect();
            const containerRect = container.getBoundingClientRect();

            // console.log('bottom', bottom, 'containerRect', containerRect.bottom)
            // Check if the bottom of the last section is less than or equal to the container's bottom
            const threshold = 10;

            const isLastSectionFullyVisible = bottom <= (containerRect.bottom + threshold);
            // Hide the scroll button if the last section is fully visible
            setShowScrollButton(!isLastSectionFullyVisible);
        }
    }, []);


    useEffect(() => {
        const container = sectionRootContainerRef.current;

        const handleScroll = () => {
            checkScrollButtonVisibility();
        };

        container.addEventListener('scroll', handleScroll);

        // Initial check
        checkScrollButtonVisibility();

        return () => {
            container.removeEventListener('scroll', handleScroll);
        };
    }, [checkScrollButtonVisibility]);

    useEffect(() => {
        if (sectionRootContainerRef.current) {
            // Scrolling to the top of the container on component mount
            sectionRootContainerRef.current.scrollTop = 0;
        }
    }, []);

    const allData = useMemo(() => {
        const { tabs, financeCategories, loanData } = tabData;
        let currentTabData = tabs && tabs[tabName] != undefined ? tabs[tabName] : null;
        // console.log('currentTabData', currentTabData);

        const sortedFinanceCategories = financeCategories?.filter((item) => item.feTab == tabName && item.categoryOrder).sort((a, b) => {
            if (a?.categoryOrder === null) return 1;
            if (b?.categoryOrder === null) return -1;

            return a?.categoryOrder - b?.categoryOrder;
        });

        const categories = sortedFinanceCategories?.map((item) => item.name);

        // console.log('categories', categories, sortedFinanceCategories)

        if (!currentTabData || !currentTabData.length) {
            if (sortedFinanceCategories && sortedFinanceCategories.length) {
                return {

                    formattedTabsData: categories.reduce((prev, curVal) => {
                        prev[curVal] = [];
                        return prev;
                    }, {}),
                    categories: sortedFinanceCategories.map((item) => {
                        return {
                            category: item?.name,
                            categoryId: item?.id,
                            isAddChannels: item?.isAddChannels
                        }
                    })
                }
            } else {
                return {
                    formattedTabsData: null,
                    categories: []
                }
            }
        }

        console.log('categories', categories)
        // console.log('financeCategories', financeCategories)

        const financeCategoriesObj = financeCategories.reduce((prev, curVal) => {
            prev[curVal.name] = {
                categoryId: curVal?.id,
                isAddChannels: curVal?.isAddChannels
            };
            return prev;
        }, {});

        // currentTabData = currentTabData.filter((item) => item.fsmId != null);

        const orderedCurrentTabData = currentTabData.sort((a, b) => {
            if (a.fsmOrder === null) return 1;
            if (b.fsmOrder === null) return -1;

            // Sorting by fsmOrder where both are not null
            return a.fsmOrder - b.fsmOrder;
        });

        // const categories = new Set<string>();


        // console.log('financeCategoriesObj', financeCategoriesObj);

        const formattedTabsData = currentTabData.reduce((prev, curVal) => {
            if (prev[curVal.categoryName] == undefined) {
                if (curVal.fsmId) {
                    prev[curVal.categoryName] = [curVal]
                } else {
                    prev[curVal.categoryName] = []
                }
            } else {
                if (curVal.fsmId) {
                    prev[curVal.categoryName] = [...prev[curVal.categoryName], curVal]
                }
            }
            // categories.add(curVal.categoryName)
            return prev;
        }, {});


        // const dummyConfig = {
        //     [PcSectionSteps.addExpenses]: ["Other Direct Cost"],
        //     [PcSectionSteps.otherExpenses]: ["Operating Expenses"]
        // }

        // // console.log('formattedTabs', formattedTabsData)
        // if (tabName in dummyConfig) {
        //     const categoriesToAdd = dummyConfig[tabName];
        //     categoriesToAdd.forEach((category) => {
        //         categories.add(category);
        //     });
        // }




        const formattedCategories = Array.from(categories as string[]).reduce((prev, curVal) => {
            prev.push({
                category: curVal,
                categoryId: financeCategoriesObj[curVal] ? financeCategoriesObj[curVal]['categoryId'] : '',
                isAddChannels: financeCategoriesObj[curVal] ? financeCategoriesObj[curVal]['isAddChannels'] : true
            })
            return prev;
        }, []);

        if (loanData && loanData.length && tabName == PcSectionSteps.otherExpenses) {
            const outstandingFsIds = loanData?.reduce((prev, curVal) => {
                if (!curVal.outstandingAmount && curVal.outstandingAmount != 0) {
                    prev.push(curVal.metricId);
                    return prev;
                } else {
                    const enteredOn = moment(curVal?.outstandingAmountEnteredOn, "MM-YYYY");
                    const currentMonth = moment();
                    // console.log('enteredOn', enteredOn.toDate(), !enteredOn.isSame(currentMonth, "month"))

                    const endDate = moment(curVal?.endDate, "MM-YYYY");
                    if (endDate?.isAfter(currentMonth, "month")) {
                        if (!enteredOn.isSame(currentMonth, "month")) {
                            prev.push(curVal.metricId)
                        }
                    }
                    return prev;
                }
            }, []);
            setOutstandingLoanFsMetricIds(new Set(outstandingFsIds))
        }


        // console.log('formattedCategories', formattedCategories)
        return {
            formattedTabsData,
            taxInput: tabData['taxInput'],
            categories: formattedCategories
        }
    }, [tabName, tabData]);

    // console.

    const scrollToElement = (element) => {
        const container = sectionRootContainerRef?.current;
        // console.log('element', element, 'container', container)
        if (element && container) {

            const elementRect = element.getBoundingClientRect();
            const containerRect = container.getBoundingClientRect();
            // console.log('elementRect', elementRect, 'containerRect', containerRect)
            container.scrollBy({ top: elementRect.top - containerRect.top, behavior: 'smooth' });
        }
    };





    useEffect(() => {
        const currentHash = window.location.hash;
        const section = searchParams.get('section');
        const card = searchParams.get('card');
        // console.log('section', section, 'card', card);
        if (section && sectionRefs.current[section]) {
            // console.log('sectionRefs.current[section]', sectionRefs.current[section])
            if (!card) {
                // sectionRefs.current[section].current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                scrollToElement(sectionRefs.current[section].current);
                searchParams.delete('section');
                searchParams.delete('card');
                setSearchParams(searchParams);
            }
        }

        // Check if there was a hash and it's been removed
        if (currentHash && window.location.hash !== currentHash) {
            window.location.hash = currentHash; // Restore the hash manually
        }
    }, [allData])

    useEffect(() => {
        if (refreshFinanceStatement) {
            refreshTabs();
            dispatch(setRefreshFinanceStatement(false));
        }
    }, [refreshFinanceStatement])







    const ipConfigs = useMemo(() => {
        if (integrationPartners != undefined) {
            let configObj = {};
            const connectionStatusSet = new Set(connectionStatus);

            for (const ip of Object.values(integrationPartners)) {
                // console.log('ip', ip)
                const logo = ip['logo'];
                const ipId = ip["_id"]
                // if(connectionStatusSet.has(ip['_id'])) {
                configObj[ip['companyName']] = {
                    connectionStatus: connectionStatusSet.has(ipId) ? true : false,
                    logo
                };
                // } 
            }
            return configObj;
        } else {
            return {};
        }
    }, [integrationPartners, connectionStatus])






    const monthLabels = useMemo(() => {
        return getLast12CYMonthsArr();
    }, []);

    return (
        <div className={`${styles.sectionRootContainer} ${!isFirstTimeUser && tabName == PcSectionSteps.otherExpenses ? styles.extraGap : ''}`} ref={sectionRootContainerRef}>
            <div className={styles.sectionWrapper}>
                {tabName == PcSectionSteps.otherExpenses ?
                    <div className={styles.sectionHeadingContainer}>
                        <div className={styles.sectionHeading}>
                            Track Monthly EBITDA and Net Cash
                        </div>
                        <div className={styles.sectionSubHeading}>
                            The P&L Tracker will only track unit economics data till CM2 if you do not fill this section.
                        </div>
                    </div> : null}
                {isFirstTimeUser && tabName == PcSectionSteps.addSalesChannels ?
                    <div ref={ref => {
                        sectionRefs.current['taxes'] = ref;
                    }} style={{ width: "600px" }} >
                        <TaxInputForm ref={taxInputRef} taxInputData={allData?.taxInput} />
                    </div>
                    : !isFirstTimeUser && tabName == PcSectionSteps.addSalesChannels ?
                        <div className={styles.taxesOuterContainer} ref={ref => {
                            sectionRefs.current['taxes'] = ref;
                        }} >
                            <div className={styles.taxesTitle}>
                                Taxes
                            </div>
                            <SecondUserTaxForm refreshTabs={refreshTabs} taxInputData={allData?.taxInput} />
                        </div> : null}
                {allData?.categories.map((categoryValue, outerIndex) => {
                    let { category, categoryId, isAddChannels } = categoryValue;
                    const sectionData = allData?.formattedTabsData[category];
                    sectionRefs.current[category] = sectionRefs.current[category] || createRef();
                    const isManageAccount = manageAccountCategory.includes(category);

                    const isLastSection = outerIndex === allData.categories.length - 1;
                    return (
                        <>
                            <div ref={sectionRefs.current[category]} className={styles.sectionContainer} key={category}>

                                <div className={styles.sectionHeaderContainer}>
                                    <div className={styles.sectionTitleContainer}>
                                        {subHeadingMapping[category] != undefined ? <div className={styles.sectionSubHeading}>
                                            {subHeadingMapping[category]}
                                        </div> : null}
                                        <div className={styles.sectionTitle}>
                                            {category}
                                        </div>
                                    </div>

                                    {isAddChannels ? <div className={styles.btnContainer}>
                                        <MeritoButton onClick={() => {
                                            // console.log('secion data', sectionData)
                                            // return;
                                            if (sectionData && sectionData.length) {
                                                setFinanceStatementId(sectionData[0].fsId);
                                            }
                                            setFormCategoryId(categoryId)

                                            setFormCategoryName(category);
                                            setActionType(formActionTypes.add);
                                            // console.log('category', category)
                                            if (category == PnlFsNames.loanServicing) {
                                                setOpenLoanForm(true);
                                            } else {
                                                setOpenForm(true);
                                            }
                                        }} type='tertiary' size='md' btnText={category == PnlFsNames.loanServicing ? `Add a Loan` : `Add ${category}`} icon={<AddIcon />} />
                                    </div> : null}
                                </div>
                                {
                                    Object.keys(infoBannerCopy).includes(category) ?
                                        <div className={styles.infoBanner}>
                                            <TbBulb />
                                            <p>{infoBannerCopy[category]}</p>
                                        </div> : null
                                }
                                {sectionData && sectionData?.length ? <div className={styles.cardContainer}>
                                    {sectionData?.map((item, index) => {
                                        console.log('here item', item)
                                        const isDataSourcePresent = item?.fsmDataSource && item?.fsmDataSource.length;
                                        const ipLogo = isDataSourcePresent && ipConfigs[item?.fsmDataSource[0]] ? ipConfigs[item?.fsmDataSource[0]]['logo'] : null;
                                        const connectionStatus = isDataSourcePresent && ipConfigs[item?.fsmDataSource[0]] ? ipConfigs[item?.fsmDataSource[0]]['connectionStatus'] : false;

                                        const cardKey = `${category}-${item.feMetricName}`;
                                        const isOutstanding = outstandingLoanFsMetricIds.has(item?.fsmId);
                                        return (
                                            <div className={styles.cardBorderContainer}>
                                                <CardDetails
                                                    fetchingIpStatus={fetchingIpStatus}
                                                    amzAdsData={amzAdsData}
                                                    expiredAccountsConfig={expiredAccountsConfig}
                                                    fsMetricItem={item}
                                                    ipConfigs={ipConfigs}
                                                    sectionData={sectionData}
                                                    category={category}
                                                    isManageAccount={isManageAccount}
                                                    financeStatmentOptions={financeStatmentOptions}
                                                    connectionToggle={connectionToggle}
                                                    monthLabels={monthLabels}
                                                    metricOptions={metricOptions}
                                                    refreshTabs={refreshTabs}
                                                    currentCardKey={cardKey}
                                                    ref={sectionRootContainerRef}
                                                    productFetchingStatus={productFetchingStatus}
                                                    isOutstanding={isOutstanding}
                                                />
                                            </div>

                                        )
                                    })}
                                </div> :
                                    <div className={styles.coldStateContainer}>
                                        <div className={styles.iconContainer}>
                                            <StopIcon />
                                        </div>
                                        <div className={styles.coldStateText}>
                                            {category == "Loan Servicing" ? 'No Loans added!' : `No ${category} added!`}
                                        </div>

                                    </div>
                                }
                            </div>
                        </>
                    )
                })}
            </div>
            {openForm ? <CategoryForm financeStatmentId={financeStatementId} financeStatementOptions={financeStatmentOptions} ipConfigs={ipConfigs} categoryId={formCategoryId} formMetricId={formMetricId} monthLabels={monthLabels} metricOptions={metricOptions} isOpen={openForm} setOpen={setOpenForm} closeForm={closeForm} actionType={actionType} categoryName={formCategoryName} /> : null}
            {openLoanForm ? <LoanForm isOpen={openLoanForm} setOpen={setOpenLoanForm} closeForm={closeForm} actionType={actionType} categoryId={formCategoryId} categoryName={formCategoryName} monthLabels={monthLabels} formMetricId={formMetricId} ipConfigs={ipConfigs} /> : null}
            <div className={styles.scrollBtnContainer}>
                {showScrollButton && <MeritoButton onClick={scrollToNextSection} isIconToRight={true} btnText="Scroll to Next Section" type="secondary-1" icon={<DownArrowIcon />} />}
            </div>
        </div>


    );
});

export default OtherSections;